import React from 'react';
import Helmet from 'react-helmet';
import type { GetServerSideProps } from 'next';
import type { ThemeSearchRequest } from '@zola/svc-web-api-ts-client';
import { fetchUser } from 'api/server/users';
import { initializeStore } from 'src/store/nextStore';
import { receiveUser } from 'actions/UserActions';
import {
  parseNextQuery,
  mapFiltersToThemeSearchRequestWebsiteV2,
} from 'pages/WebsiteDesignsPLPSSR/helpers';
import { serverFacetedThemeSearchWebsiteV2 } from 'api/server/themes';
import {
  receiveThemeSearch,
  updateFilters,
  updateSort,
} from 'pages/WebsiteDesignsPLPSSR/reducer/websitePlpActions';

import WebsiteDesignsPLPV2 from 'pages/WebsiteDesignsPLPV2';

import { Device } from '../../../src/lib/universal/utils/devices';

type PreauthWebsiteDesignsPLPContainerProps = {
  isDesktop: boolean;
};

const PreauthWebsiteDesignsPLPContainer: React.FC<PreauthWebsiteDesignsPLPContainerProps> = ({
  isDesktop,
}) => (
  <>
    <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />
    <WebsiteDesignsPLPV2 isDesktop={isDesktop} />
  </>
);

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }) => {
  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;

  const userContext = await fetchUser(req);

  if (userContext?.has_completed_onboarding) {
    // Redirect to the post--auth designs PLP
    res.writeHead(302, {
      Location: '/wedding/manage/website/designs',
    });
    res.end();
    return { props: {} }; // stop execution
  }
  if (userContext) {
    dispatch(receiveUser(userContext));
  }

  const { filters, sortKey } = parseNextQuery(query);

  const themesRequest = mapFiltersToThemeSearchRequestWebsiteV2({ filters, sortKey });
  const fetchThemesListPromise = await serverFacetedThemeSearchWebsiteV2(themesRequest, req).then(
    json => {
      dispatch(receiveThemeSearch(json));
      dispatch(updateFilters(filters));
      dispatch(updateSort((sortKey as unknown) as ThemeSearchRequest.SortKeyEnum));
    }
  );

  const userAgent = req.headers['user-agent'];
  const device = new Device(userAgent);

  try {
    await Promise.all([fetchThemesListPromise]);
  } catch (e) {
    throw new Error(`Data fetching error on /wedding-planning/website/designs: ${e}`);
  }

  const initialReduxState = JSON.stringify(getState());
  return {
    props: {
      initialReduxState,
      isDesktop: device.isDesktop(),
    },
  };
};

export default PreauthWebsiteDesignsPLPContainer;
